// ========================================
// COMPONENTE DE LOGIN COM TROCA DE SENHA
// frontend/src/components/Login.jsx
// ========================================

import React, { useState } from 'react';
import { LogIn, User, Lock, AlertCircle, Loader, X, Eye, EyeOff, CheckCircle, Key } from 'lucide-react';
import { api } from '../config/api';
import { sanitizeString } from '../utils/security';

// ========================================
// MODAL DE TROCAR SENHA
// ========================================
const ModalTrocarSenha = ({ isOpen, onClose }) => {
  const [username, setUsername] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);

  const resetForm = () => {
    setUsername('');
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validarFormulario = () => {
    if (!username.trim()) {
      setError('Informe seu usuário');
      return false;
    }
    if (!senhaAtual) {
      setError('Informe sua senha atual');
      return false;
    }
    if (novaSenha.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (!/\d/.test(novaSenha) || !/[a-zA-Z]/.test(novaSenha)) {
      setError('A nova senha deve conter letras e números');
      return false;
    }
    if (novaSenha !== confirmarSenha) {
      setError('As senhas não coincidem');
      return false;
    }
    if (senhaAtual === novaSenha) {
      setError('A nova senha deve ser diferente da atual');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validarFormulario()) return;

    setLoading(true);

    try {
      const data = await api.post('/auth/alterar-senha', {
        username: username.trim().toLowerCase(),
        senhaAtual,
        novaSenha
      }, false);

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(data.message || 'Erro ao alterar senha');
      }
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Key size={24} className="text-white" />
            <h2 className="text-xl font-bold text-white">Alterar Senha</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-2">Senha Alterada!</h3>
              <p className="text-gray-600">Sua senha foi alterada com sucesso.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Para alterar sua senha, informe seus dados abaixo.
              </p>

              {/* Usuário */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="seu.usuario"
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Senha Atual */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Senha Atual
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type={mostrarSenhaAtual ? 'text' : 'password'}
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenhaAtual(!mostrarSenhaAtual)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {mostrarSenhaAtual ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Nova Senha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type={mostrarNovaSenha ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarNovaSenha(!mostrarNovaSenha)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {mostrarNovaSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {/* Requisitos */}
                {novaSenha && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className={`px-2 py-0.5 rounded ${novaSenha.length >= 6 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      6+ caracteres
                    </span>
                    <span className={`px-2 py-0.5 rounded ${/[a-zA-Z]/.test(novaSenha) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Letras
                    </span>
                    <span className={`px-2 py-0.5 rounded ${/\d/.test(novaSenha) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Números
                    </span>
                  </div>
                )}
              </div>

              {/* Confirmar Nova Senha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={confirmarSenha}
                    onChange={(e) => setConfirmarSenha(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    disabled={loading}
                  />
                </div>
                {confirmarSenha && novaSenha && (
                  <p className={`mt-1 text-xs flex items-center gap-1 ${novaSenha === confirmarSenha ? 'text-green-600' : 'text-red-600'}`}>
                    {novaSenha === confirmarSenha ? (
                      <><CheckCircle size={12} /> Senhas coincidem</>
                    ) : (
                      <><AlertCircle size={12} /> Senhas não coincidem</>
                    )}
                  </p>
                )}
              </div>

              {/* Erro */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-2.5 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      Alterando...
                    </>
                  ) : (
                    'Alterar Senha'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ========================================
// COMPONENTE PRINCIPAL DE LOGIN
// ========================================
const Login = ({ onLoginSuccess = null }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const [mostrarModalSenha, setMostrarModalSenha] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPendingApproval(false);
    setLoading(true);

    try {
      // Sanitiza o username antes de enviar
      const cleanUsername = sanitizeString(username.trim().toLowerCase());
      
      console.debug('Enviando login:', { username: cleanUsername });

      const data = await api.post('/auth/login', {
        username: cleanUsername,
        password: password
      }, false);

      console.debug('Resposta do /api/auth/login:', data);

      if (data.success) {
        console.debug('Login bem sucedido:', {
          primeiroAcesso: data.primeiroAcesso,
          precisa_trocar_senha: data.usuario?.precisa_trocar_senha
        });

        // Chamar callback de sucesso - o App.js vai verificar se precisa trocar senha
        if (typeof onLoginSuccess === 'function') {
          try {
              onLoginSuccess(data.usuario);
          } catch (cbErr) {
            console.error('Erro dentro de onLoginSuccess:', cbErr);
            window.location.reload();
          }
        } else {
          // Se não tem callback, recarrega a página para o App.js decidir
          window.location.reload();
        }
      } else {
        if (data.pendingApproval) {
          setPendingApproval(true);
        }
        const msg = data.message || 'Erro ao fazer login';
        setError(msg);
      }
    } catch (err) {
      console.error('Erro no login (catch externo):', err);
      if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
        setError('Erro ao conectar com o servidor. Verifique se o backend está rodando.');
      } else {
        setError(err.message || 'Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Fundo gradiente fixo que cobre toda viewport */}
      <div className="auth-bg" />
      <div className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md">
          {/* Logo/Título */}
          <div className="mb-8">
            <div className="flex items-center justify-between bg-white/95 backdrop-blur-md px-8 py-4 rounded-2xl shadow-2xl">
              {/* Logo InovaFlow */}
              <div className="flex flex-col items-start justify-center">
                <span className="text-xl font-bold text-blue-700 leading-tight tracking-tight">
                  InovaFlow
                </span>
                <span className="text-xs text-gray-600 tracking-wide font-medium">
                  Sistema de Gestão Empresarial
                </span>
              </div>
              {/* Barra Divisória */}
              <div className="w-px h-14 bg-gray-300 mx-4"></div>
              {/* Logo Primalia */}
              <img 
                src="/logo-primalia.svg" 
                alt="Primália PVC" 
                className="h-20 w-auto object-contain"
              />
            </div>
          </div>

          {/* Card de Login */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <User size={32} className="text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Bem-vindo!</h2>
              <p className="text-gray-600 mt-2">Entre com suas credenciais informadas pela empresa</p>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Campo Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="seu.usuario"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    required
                    disabled={loading}
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Campo Senha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {/* Mensagem de Erro */}
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                    {pendingApproval && (
                      <p className="text-xs text-red-700 mt-2">
                        Sua conta foi criada mas precisa ser aprovada por um administrador.
                        Você receberá acesso em breve.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Botão de Login */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    <span>Entrando...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Entrar</span>
                  </>
                )}
              </button>
            </form>

            {/* Informações Adicionais */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                Primeiro acesso? Utilize a senha provisória fornecida pela empresa e defina sua senha definitiva.
              </p>
              
              {/* Link para trocar senha */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMostrarModalSenha(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  Deseja trocar sua senha? Clique aqui
                </button>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                Problemas para acessar? Entre em contato com o suporte.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-white text-sm">
              © 2026 Inovacom Software - Todos os direitos reservados
            </p>
          </div>
        </div>
      </div>

      {/* Modal de Trocar Senha */}
      <ModalTrocarSenha 
        isOpen={mostrarModalSenha} 
        onClose={() => setMostrarModalSenha(false)} 
      />
    </>
  );
};

export default Login;